import React, { useEffect, useRef } from 'react';
import './EmotionBars.css';

/**
 * EmotionBars — Displays animated horizontal progress bars for each emotion.
 * Props:
 *   expressions: object with emotion keys mapped to 0–1 float values
 *                e.g. { happy: 0.72, angry: 0.12, sad: 0.08, fearful: 0.05, surprised: 0.03 }
 *   compact:     boolean — if true renders a smaller variant (for news cards)
 */

const EMOTION_MAP = [
  { key: 'happy',     label: 'Joy',      emoji: '😊', colorVar: '--emotion-joy',      softVar: '--emotion-joy-soft'      },
  { key: 'angry',     label: 'Anger',    emoji: '😡', colorVar: '--emotion-anger',    softVar: '--emotion-anger-soft'    },
  { key: 'sad',       label: 'Sadness',  emoji: '😢', colorVar: '--emotion-sadness',  softVar: '--emotion-sadness-soft'  },
  { key: 'fearful',   label: 'Fear',     emoji: '😨', colorVar: '--emotion-fear',     softVar: '--emotion-fear-soft'     },
  { key: 'surprised', label: 'Surprise', emoji: '😲', colorVar: '--emotion-surprise', softVar: '--emotion-surprise-soft' },
];

/**
 * Generates simulated emotion percentages seeded on a dominant emotion.
 * Used when we only have the detected emotion name (e.g. 'happy') but not
 * the full expressions object.
 */
export const simulateExpressions = (dominantEmotion, seed = 0) => {
  const keyMap = {
    happy: 'happy', joy: 'happy', neutral: 'happy',
    sad: 'sad', sadness: 'sad',
    angry: 'angry', anger: 'angry',
    fearful: 'fearful', fear: 'fearful',
    surprised: 'surprised', surprise: 'surprised',
  };

  const dominant = keyMap[dominantEmotion?.toLowerCase()] || 'happy';
  // Use seed for stable pseudo-random values per article
  const rng = (n) => {
    const x = Math.sin(seed * 12.9898 + n * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  const keys = EMOTION_MAP.map(e => e.key);
  const raw = {};
  keys.forEach((k, i) => {
    raw[k] = k === dominant ? 0.55 + rng(i) * 0.35 : rng(i) * 0.25;
  });

  // Normalize to sum = 1
  const total = Object.values(raw).reduce((a, b) => a + b, 0);
  const normalized = {};
  keys.forEach(k => { normalized[k] = raw[k] / total; });
  return normalized;
};

const EmotionBars = ({ expressions, compact = false }) => {
  const barsRef = useRef([]);

  // Find dominant emotion
  const dominantKey = expressions
    ? Object.keys(expressions).reduce((a, b) => (expressions[a] || 0) > (expressions[b] || 0) ? a : b)
    : null;

  // Animate bars in on mount
  useEffect(() => {
    const timers = [];
    barsRef.current.forEach((bar, i) => {
      if (!bar) return;
      const emo = EMOTION_MAP[i];
      const pct = Math.round((expressions?.[emo.key] || 0) * 100);
      // Start at 0, animate to target width
      bar.style.width = '0%';
      const t = setTimeout(() => {
        bar.style.width = `${pct}%`;
      }, 80 + i * 60);
      timers.push(t);
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, [expressions]);

  if (!expressions) return null;

  return (
    <div className={`emotion-bars ${compact ? 'emotion-bars--compact' : ''}`} aria-label="Emotion distribution">
      {EMOTION_MAP.map((emo, i) => {
        const pct = Math.round((expressions[emo.key] || 0) * 100);
        const isDominant = emo.key === dominantKey;
        return (
          <div
            key={emo.key}
            className={`emotion-bar-row ${isDominant ? 'emotion-bar-row--dominant' : ''}`}
          >
            <div className="emotion-bar-label">
              <span className="emotion-bar-emoji" role="img" aria-label={emo.label}>{emo.emoji}</span>
              {!compact && <span className="emotion-bar-name">{emo.label}</span>}
              <span className="emotion-bar-pct">{pct}%</span>
            </div>
            <div
              className="emotion-bar-track"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={emo.label}
            >
              <div
                ref={el => (barsRef.current[i] = el)}
                className="emotion-bar-fill"
                style={{
                  background: `var(${emo.colorVar})`,
                  boxShadow: isDominant ? `0 0 8px var(${emo.colorVar})` : 'none',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmotionBars;
